/**
 * Formatting utilities for display
 */

/**
 * Formats a phone number for display
 * @param phone - Phone number string
 * @returns Formatted phone number
 */
export function formatPhoneNumber(phone: string): string {
  if (!phone) return ''
  // Remove any non-digit characters
  let cleaned = phone.replace(/\D/g, '')
  
  // Convert +233 format to 0 format (Ghana numbers)
  if (cleaned.startsWith('233') && cleaned.length === 12) {
    cleaned = '0' + cleaned.slice(3)
  }
  
  // Format as 0XX XXX XXXX
  if (cleaned.length === 10) {
    return `${cleaned.slice(0, 3)} ${cleaned.slice(3, 6)} ${cleaned.slice(6)}`
  }
  return phone
}

/**
 * Normalizes phone number to 0 format (removes +233 prefix)
 * @param phone - Phone number string
 * @returns Normalized phone number starting with 0
 */
export function normalizePhoneNumber(phone: string): string {
  if (!phone) return ''
  // Remove any non-digit characters
  let cleaned = phone.replace(/\D/g, '')
  
  // Convert +233 format to 0 format (Ghana numbers)
  if (cleaned.startsWith('233') && cleaned.length === 12) {
    cleaned = '0' + cleaned.slice(3)
  }
  
  return cleaned
}

/**
 * Converts phone number from 0 format to +233 format for API
 * @param phone - Phone number string (0XX format)
 * @returns Phone number in +233 format
 */
export function convertToApiPhoneFormat(phone: string): string {
  if (!phone) return ''
  
  // Remove any non-digit characters
  let cleaned = phone.replace(/\D/g, '')
  
  // If already in +233 format, return as is
  if (phone.startsWith('+233')) {
    return phone
  }
  
  // Convert from 0 format to +233 format
  if (cleaned.startsWith('0') && cleaned.length === 10) {
    return '+233' + cleaned.slice(1)
  }
  
  // If it's already in 233 format without +
  if (cleaned.startsWith('233') && cleaned.length === 12) {
    return '+' + cleaned
  }
  
  // Default: assume it's in 0 format
  if (cleaned.length === 10) {
    return '+233' + cleaned.slice(1)
  }
  
  return phone
}

/**
 * Formats an amount as currency
 * @param amount - Amount to format
 * @param currency - Currency code (default: GHS)
 * @returns Formatted currency string
 */
export function formatCurrency(amount: number, currency: string = 'GHS'): string {
  return new Intl.NumberFormat('en-GH', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount)
}

/**
 * Formats a date for display
 * @param dateString - ISO date string
 * @returns Formatted date string
 */
export function formatDate(dateString: string): string {
  const date = new Date(dateString)
  return new Intl.DateTimeFormat('en-GH', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date)
}

